import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "./ui/Primitives";

// Mirrors backend PLAN_MODULES in auth_utils.py.
export const PLAN_MODULES = {
  none:    new Set(),
  kueche:  new Set(["kitchen", "ai", "menus", "reports"]),
  bar:     new Set(["bar", "ai", "menus", "reports"]),
  pro:     new Set(["kitchen", "bar", "ai", "allergens", "waste", "engineering", "menus", "reports", "invoices"]),
  hotel:   new Set(["kitchen", "bar", "ai", "allergens", "waste", "engineering", "hotel", "menus", "reports", "invoices"]),
  premium: new Set(["kitchen", "bar", "ai", "allergens", "waste", "engineering", "hotel", "multi_location", "menus", "reports", "invoices"]),
};

export function hasModule(user, mod) {
  if (!user || user === false) return false;
  if (user.role === "super_admin") return true;
  // Prefer the modules array resolved by the backend (handles team-owner inheritance)
  if (Array.isArray(user.modules) && user.modules.length > 0) {
    return user.modules.includes(mod);
  }
  const set = PLAN_MODULES[user.plan || "none"] || PLAN_MODULES.none;
  return set.has(mod);
}

// Phase 34 — Per-role sidebar nav visibility.
// Owners (no team_role) and super_admin see every nav item (subject to plan-gate).
// Team members see only the nav items their role is allowed to see.
// Nav keys mirror the IDs used in AppShell (dashboard, ingredients, recipes,
// cocktails, menus, event-menus, hotel, allergens, waste, engineering, ai,
// reports, invoices, settings, billing, outlets, unified-dashboard).
const ROLE_NAV = {
  gm: new Set([
    "dashboard", "ingredients", "recipes", "cocktails", "menus", "event-menus",
    "hotel", "allergens", "waste", "engineering", "ai", "plating", "drink", "reports", "invoices",
    "settings",
  ]),
  fnb_manager: new Set([
    "dashboard", "ingredients", "recipes", "cocktails", "menus", "event-menus",
    "allergens", "waste", "engineering", "ai", "plating", "drink", "reports",
  ]),
  kitchen_chef: new Set([
    "dashboard", "ingredients", "recipes", "menus", "allergens", "waste", "ai", "plating",
  ]),
  bar_manager: new Set([
    "dashboard", "ingredients", "cocktails", "menus", "allergens", "waste", "ai", "drink",
  ]),
  service: new Set([
    "dashboard", "recipes", "cocktails", "menus",
  ]),
  // Legacy generic team roles (kept for backwards compatibility with seed accounts)
  viewer: new Set([
    "dashboard", "ingredients", "recipes", "cocktails", "menus", "event-menus",
    "allergens", "engineering", "reports",
  ]),
  editor: new Set([
    "dashboard", "ingredients", "recipes", "cocktails", "menus", "event-menus",
    "hotel", "allergens", "waste", "engineering", "ai", "plating", "drink", "reports", "invoices",
    "settings",
  ]),
};

export function canSeeNav(user, navKey) {
  if (!user || user === false) return false;
  if (user.role === "super_admin") return true;
  const teamRole = user.team_role;
  // Owners (no team_role) see every nav item — plan-gates handle locked badges.
  if (!teamRole) return true;
  const allowed = ROLE_NAV[teamRole];
  if (!allowed) return true; // unknown role — fail open to avoid hiding everything
  return allowed.has(navKey);
}

export function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-void">
        <Spinner size={28} />
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (adminOnly && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export function OwnerOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-void">
        <Spinner size={28} />
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }
  // Phase 33 — team members cannot access owner-only pages (e.g. billing)
  if (user.team_owner_id) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export function PlanGate({ module, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-void">
        <Spinner size={28} />
      </div>
    );
  }
  if (!hasModule(user, module)) {
    return <Navigate to={`/billing?upgrade=${encodeURIComponent(module)}&from=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return children;
}

export function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-void">
        <Spinner size={28} />
      </div>
    );
  }
  if (user && user !== false) {
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
  }
  return children;
}
